import React, { useEffect, useState } from 'react'
import { fmt } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { CompanyPicker, costGet, useCompany, useMeta } from './CostCommon.jsx'

// 원가관리 · 마감 현황.
// 월 마감은 순서가 곧 정확성이라(원가 확정 전에 수불부에 단가를 넣으면 재고금액이 통째로
// 틀린다) 어디까지 왔는지를 맨 위에 세운다. 요약 숫자는 그 아래.

export default function CostDashboard({ month, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [company, setCompany] = useCompany()
  const [meta] = useMeta()
  const [status, setStatus] = useState(null)
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    setErr(''); setStatus(null); setData(null)
    Promise.all([
      costGet('/close-status', { month, company }),
      costGet('/summary', { month, company }),
    ]).then(([s, d]) => { setStatus(s); setData(d) }).catch((e) => setErr(e.message))
  }, [month, company])

  const done = status ? status.steps.filter((s) => s.ok).length : 0

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>{month} 원가 마감 현황</h2>
        <NoteDrawer target="dashboard" screenKey="cost_dashboard"
          context={{ month }} contextLabel={month}
          onNavigate={(ctx) => ctx?.month && onMonthChange?.(ctx.month)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <CompanyPicker value={company} onChange={setCompany} meta={meta} />
      </div>
      {err && <div className="error-box">{err}</div>}
      {!status && !err && <div className="muted">불러오는 중…</div>}

      {status && (
        <div className="panel">
          <h3>마감 진행 {done} / {status.steps.length}</h3>
          <table>
            <thead>
              <tr><th style={{ width: 40 }}>단계</th><th className="l">하는 일</th><th className="l">상태</th><th style={{ width: 60 }}>판정</th></tr>
            </thead>
            <tbody>
              {status.steps.map((s) => (
                <tr key={s.no}>
                  <td className="c">{s.no}</td>
                  <td className="l">{s.label}</td>
                  <td className="l" style={s.ok ? undefined : { color: 'var(--bad)' }}>{s.detail}</td>
                  <td className="c">{s.ok ? <span className="ok">✓</span> : <span className="warn">·</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="hint">
            기초데이터 → 제조·구매원가 명세 → 원가산출 → 물류팀 수불부 수취 → 원가 산입 → 재고금액 → 충당금.
            앞 단계가 비어 있으면 뒤 단계 숫자는 믿을 수 없습니다.
          </div>
          {status.amortAmt > 0 && (
            <div className="hint">
              <b>라이선스 선급금 상각 {fmt(status.amortAmt)}원</b> — 연초에 선지급한 돈을 이 달 생산분만큼 원가로 옮긴 금액입니다.
              원장에 매입이 없는 게 정상이고, 결산분개 화면의 <b>전표 3</b>(차: 직접경비 / 대: 선급금)만 올리면 됩니다.
              잔액 소진 현황은 기초데이터 화면의 &lsquo;라이선스 선급금&rsquo;에서 봅니다.
            </div>
          )}
          {status.unbooked?.length > 0 && (
            <div className="hint warn-hint">
              <b>미계상 원가 {status.unbooked.length}건 · {fmt(status.unbookedAmt)}원</b> —
              명세에는 잡혀 있는데 계정별원장에는 없습니다
              ({status.unbooked.map((u) => `${u.label} ${fmt(u.gap)}원`).join(', ')}).
              타팀은 배부를 마쳤는데 세금계산서 수취나 지급이 아직 안 된 것으로 보입니다.
              원래는 선급금·미지급금으로 잡혀 있어야 할 금액이니,
              <b> 기초데이터 화면 아래 &lsquo;미계상 원가&rsquo;</b>에서 받을 계정을 정하고 기록해 두세요.
            </div>
          )}
        </div>
      )}

      {data && (
        <>
          <div className="cards">
            <div className="card"><div className="label">총 생산량</div><div className="value">{fmt(data.totalQty)} EA</div></div>
            <div className="card"><div className="label">총 제조원가</div><div className="value">{fmt(data.grandTotal)}원</div></div>
            <div className="card"><div className="label">평균 개당원가</div><div className="value">{fmt(data.avgUnitCost, 1)}원</div></div>
            <div className="card"><div className="label">기말 재고금액</div><div className="value">{fmt(status?.closingAmt)}원</div></div>
          </div>

          <div className="row" style={{ alignItems: 'flex-start' }}>
            <div className="panel" style={{ flex: '1 1 380px' }}>
              <h3>원가 구성</h3>
              <table>
                <thead><tr><th className="l">항목</th><th>금액</th><th>구성비</th><th className="l">비중</th></tr></thead>
                <tbody>
                  {data.composition.map((c) => (
                    <tr key={c.label}>
                      <td className="l">{c.label}{c.account ? ` (${c.account})` : ''}</td>
                      <td>{fmt(c.amount)}</td>
                      <td>{(c.ratio * 100).toFixed(1)}%</td>
                      <td><div className="bar"><i style={{ width: `${Math.min(100, c.ratio * 100)}%` }} /></div></td>
                    </tr>
                  ))}
                  <tr className="total"><td className="l">합계</td><td>{fmt(data.grandTotal)}</td><td>100%</td><td /></tr>
                </tbody>
              </table>
            </div>

            <div className="panel" style={{ flex: '1 1 420px' }}>
              <h3>제조원가 상위 10개 품목</h3>
              <table>
                <thead><tr><th>순위</th><th className="l">상품명</th><th className="l">옵션</th><th>총 제조원가</th><th>개당원가</th></tr></thead>
                <tbody>
                  {data.top10.length === 0 && <tr><td colSpan={5} className="c muted">해당 월 생산 데이터가 없습니다</td></tr>}
                  {data.top10.map((r) => (
                    <tr key={r.rank}>
                      <td className="c">{r.rank}</td>
                      <td className="l">{r.name}</td>
                      <td className="l">{r.option_name}</td>
                      <td>{fmt(r.totalCost)}</td>
                      <td>{fmt(r.unitCost, 1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="row" style={{ alignItems: 'flex-start' }}>
            <div className="panel" style={{ flex: '1 1 380px' }}>
              <h3>대분류별 집계</h3>
              <table>
                <thead><tr><th className="l">대분류</th><th>품목 수</th><th>생산량</th><th>총 제조원가</th><th>평균 개당원가</th></tr></thead>
                <tbody>
                  {Object.entries(data.byCategory).map(([cat, c]) => (
                    <tr key={cat}>
                      <td className="l">{cat}</td>
                      <td>{fmt(c.items)}</td>
                      <td>{fmt(c.qty)}</td>
                      <td>{fmt(c.cost)}</td>
                      <td>{c.qty > 0 ? fmt(c.cost / c.qty, 1) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="panel" style={{ flex: '1 1 420px' }}>
              <h3>월별 대사 (재구성 원가 vs 원장 기대치)</h3>
              <table>
                <thead><tr><th>월</th><th>생산량</th><th>재구성 총제조원가</th><th>원장 기대치</th><th>차이</th><th>판정</th></tr></thead>
                <tbody>
                  {data.monthly.length === 0 && <tr><td colSpan={6} className="c muted">올해 생산 데이터가 없습니다</td></tr>}
                  {data.monthly.map((m) => (
                    <tr key={m.month}>
                      <td className="c">{m.month}</td>
                      <td>{fmt(m.qty)}</td>
                      <td>{fmt(m.total)}</td>
                      <td>{fmt(m.expected)}</td>
                      <td>{fmt(m.diff, 2)}</td>
                      <td className="c">{m.ok ? <span className="ok">✓</span> : <span className="warn">⚠ 확인</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="hint">
                원장 기대치 = 계정별원장 금액(원장이 0인 계정은 명세 직접귀속액). 차이가 1원 이상이면 ⚠로 표시됩니다.
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
